/* ==========================================================================
   Better Pet & Co. — homepage behaviour

   Four small things, none of which the page depends on:

     1. the customer-photo lightbox
     2. clip autoplay, only while a tile is on screen, and never when the
        visitor has asked for reduced motion
     3. the review filter pills
     4. "show more reviews"

   Everything is rendered server-side and works without this file: the tiles
   are visible, the reviews are in the HTML, and the first six are the ones
   that show. This only adds the interaction on top, which is why the script
   is deferred and why nothing here reserves or changes layout height.
   ========================================================================== */

(() => {
  'use strict';

  /* Four sections ask for this file: the clip row, the photo wall, the review
     list and the trust bar. Four script tags means four runs, and four runs
     means every listener in here is bound four times. The lightbox opened
     four deep and needed four clicks on the cross to clear, which reads as a
     broken close button rather than as a script loaded four times.

     Same guard as bp-product.js. One run per page, whatever asks for it. */
  if (window.__bpcHomeInit) return;
  window.__bpcHomeInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- 1. Lightbox ------------------------------------------------------
     One <dialog> for the whole page, filled from the <template> next to the
     tile that was clicked. A dialog per tile would put every full-size image
     and every review body in the document twice. */

  let dialog = null;

  function ensureDialog() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'bpc-lightbox';
    dialog.innerHTML =
      '<button type="button" class="bpc-lightbox__close" aria-label="Close">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.75" stroke-linecap="round" aria-hidden="true">' +
      '<path d="M18 6 6 18M6 6l12 12"/></svg></button>' +
      '<div class="bpc-lightbox__inner"></div>';

    dialog.addEventListener('click', (event) => {
      // Clicking the backdrop closes. The dialog element itself fills only
      // the panel, so a click landing on <dialog> is a click outside it.
      if (event.target === dialog) dialog.close();
    });

    dialog.querySelector('.bpc-lightbox__close').addEventListener('click', () => dialog.close());

    dialog.addEventListener('close', () => {
      dialog.querySelectorAll('video').forEach((v) => v.pause());
      dialog.querySelector('.bpc-lightbox__inner').replaceChildren();
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bpc-lightbox]');
    if (!trigger) return;

    const key = trigger.getAttribute('data-bpc-lightbox');
    const template = document.querySelector(`[data-bpc-lightbox-content="${CSS.escape(key)}"]`);
    if (!template) return;

    const el = ensureDialog();
    el.querySelector('.bpc-lightbox__inner').replaceChildren(template.content.cloneNode(true));
    el.returnValue = '';
    el.showModal();
  });

  /* ---- 2. Clips ---------------------------------------------------------
     A tile only plays while it is on screen, and a visitor who has asked for
     reduced motion gets a still frame with controls instead of movement they
     did not ask for. */

  const clips = document.querySelectorAll('video[data-bpc-autoplay]');

  if (clips.length) {
    if (reduceMotion.matches) {
      clips.forEach((clip) => {
        clip.setAttribute('controls', '');
        clip.removeAttribute('data-bpc-autoplay');
      });
    } else if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (records) => {
          records.forEach((record) => {
            const clip = record.target;
            if (record.isIntersecting) {
              if (clip.preload === 'none') clip.preload = 'auto';
              clip.play().catch(() => {
                /* autoplay refused: the poster frame stays, which is fine */
              });
            } else {
              clip.pause();
            }
          });
        },
        { threshold: 0.4 }
      );

      clips.forEach((clip) => observer.observe(clip));
    }
  }

  /* ---- 3 & 4. Review filters and "show more" ---------------------------- */

  document.querySelectorAll('[data-bpc-reviews]').forEach((root) => {
    const items = Array.from(root.querySelectorAll('[data-bpc-review]'));
    const moreBtn = root.querySelector('[data-bpc-more]');
    const empty = root.querySelector('[data-bpc-empty]');
    const step = parseInt(root.dataset.perPage, 10) || 6;

    let filter = 'all';
    let visible = step;

    const matches = (item) => {
      if (filter === 'all') return true;
      if (filter === 'photo') return item.dataset.hasPhoto === 'true';
      if (filter.startsWith('rating-')) return item.dataset.rating === filter.slice(7);
      if (filter.startsWith('type-')) return item.dataset.type === filter.slice(5);
      return true;
    };

    const render = () => {
      let shown = 0;
      let matched = 0;

      items.forEach((item) => {
        if (!matches(item)) {
          item.classList.add('is-hidden');
          return;
        }
        matched += 1;
        if (shown < visible) {
          item.classList.remove('is-hidden');
          shown += 1;
        } else {
          item.classList.add('is-hidden');
        }
      });

      if (moreBtn) moreBtn.hidden = matched <= shown;
      if (empty) empty.hidden = matched !== 0;
    };

    root.querySelectorAll('[data-bpc-filter]').forEach((pill) => {
      pill.addEventListener('click', () => {
        filter = pill.getAttribute('data-bpc-filter');
        visible = step;

        root.querySelectorAll('[data-bpc-filter]').forEach((other) => {
          const active = other === pill;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        render();
      });
    });

    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        const first = items.find((item) => item.classList.contains('is-hidden') && matches(item));
        visible += step;
        render();
        // Move focus to the first newly revealed review rather than leaving it
        // on a button that may have just disappeared.
        if (first) {
          first.setAttribute('tabindex', '-1');
          first.focus({ preventScroll: true });
        }
      });
    }

    render();
  });

  /* ---- 5. Review strip carousel dots (phone only) ----------------------- */

  document.querySelectorAll('[data-bpc-carousel]').forEach((track) => {
    const scope = track.parentElement;
    const dots = scope.querySelectorAll('[data-bpc-dot]');
    const prev = scope.querySelector('[data-bpc-prev]');
    const next = scope.querySelector('[data-bpc-next]');
    const slides = Array.from(track.children);

    /* Arrows. Rendered hidden and shown here, because an arrow that looks
       live and does nothing is worse than no arrow: with the script blocked
       the row still scrolls by touch and by trackpad, and the control that
       would lie about it stays out of the way. */
    if (prev && next && slides.length > 1) {
      const step = () => track.clientWidth * 0.8;

      const sync = () => {
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      };

      const nudge = (direction) => {
        const left = track.scrollLeft + direction * step();
        track.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        // Smooth scrolling is dropped without an error on a scroll-snap
        // container in some browsers. If nothing moved, jump.
        const from = track.scrollLeft;
        window.setTimeout(() => {
          if (track.scrollLeft === from) track.scrollLeft = left;
          sync();
        }, 250);
      };

      prev.hidden = false;
      next.hidden = false;
      prev.addEventListener('click', () => nudge(-1));
      next.addEventListener('click', () => nudge(1));
      track.addEventListener('scroll', sync, { passive: true });
      sync();
    }

    if (!dots.length) return;

    track.addEventListener(
      'scroll',
      () => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      },
      { passive: true }
    );

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const target = slides[i];
        if (target) {
          track.scrollTo({
            left: target.offsetLeft - track.offsetLeft,
            behavior: reduceMotion.matches ? 'auto' : 'smooth',
          });
        }
      });
    });
  });
  /* ------------------------------------------------------------------ *
   * The trust band's copies.
   *
   * The band scrolls by half its width, which needs the promises repeated
   * enough times to cover a wide monitor with no seam. Six copies used to be
   * rendered server-side with aria-hidden on five of them: right for a screen
   * reader, wrong for every other thing that reads a page as text. A crawler,
   * an audit tool and anyone reading the source counted the four promises six
   * times over, and it was reported as a bug three times, which it effectively
   * was.
   *
   * So the markup carries one list and the copies are made here. Nothing
   * depends on this running: without it the band is one row of four promises,
   * which is a trust bar, just not a moving one.
   * ------------------------------------------------------------------ */
  document.querySelectorAll('[data-bpc-band-copies]').forEach((track) => {
    const original = track.querySelector('.bpc-trustbar__list');
    if (!original || track.children.length > 1) return;

    const wanted = Number(track.dataset.bpcBandCopies) || 1;
    if (wanted < 2) return;

    const frag = document.createDocumentFragment();
    for (let i = 1; i < wanted; i += 1) {
      const copy = original.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      /* Shopify's section anchors are ids; cloning them would put duplicates
         in the document and break the editor's click-to-select. */
      copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      frag.appendChild(copy);
    }
    track.appendChild(frag);
  });
})();
